import { Box, Checkbox, Dropdown, Input, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AbsenceReason,
  createNewAbsenceReason,
  generateDropdownOptions,
  generateEmptyAbsenceReason,
  generateNotification,
  updateAbsenceReason,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { Right } from 'shared/src/userRole/UserRole.types';
import { NotAllowed } from '../../components/NotAllowed';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { useUser } from '../../components/UserContext';
import { generateTableRowForLanguageTranslation } from '../../utils/InputUtils';
import { isUserAllowedToDo } from '../../utils/user/User.utils';
import { preventSubmitOnEnter } from '../../utils/general.utils';

const AbsenceReasonCreate: React.FC = () => {
  const { user, axios } = useUser();
  const form = useRef<HTMLFormElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const location = useLocation<{ reason?: AbsenceReason }>();
  const [absenceReason, setAbsenceReason] = useState<AbsenceReason>(
    location.state?.reason ? location.state.reason : generateEmptyAbsenceReason(),
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: loadedCompanies } = useData('COMPANY_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  /**
   * Submit handler that creates or updates absenceReasons
   * Without a reason in the location state the user is creating
   * with a reason in the location state, the user is editing
   * @param evt HTML FormEvent
   */
  const handleSubmit = (redirect: boolean): void => {
    if (!form.current?.checkValidity()) return;
    if (!location.state?.reason)
      //no history, so we are in create-mode
      createNewAbsenceReason(axios, absenceReason).then((createdReason) => {
        if (createdReason && redirect) navigate('/absence/reason');
        else if (createdReason) {
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.saveSuccessfull'),
          });
          setAbsenceReason(createdReason);
        }
      });
    else {
      updateAbsenceReason(axios, {
        ...absenceReason,
        lastUpdate: new Date(),
        updatedBy: user.id,
      }).then((updatedReason) => {
        if (updatedReason && redirect) navigate('/absence/reason');
        else if (updatedReason) {
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.saveSuccessfull'),
          });
          setAbsenceReason(updatedReason);
        }
      });
    }
  };
  /**
   * Rows to display and edit translations for all languages
   */
  const translationRows: TableRow[] = useMemo(
    (): TableRow[] =>
      generateTableRowForLanguageTranslation(absenceReason, 'translations', setAbsenceReason),
    [absenceReason],
  );

  return isUserAllowedToDo(user.right, Right.ABSENCE_REASON_CREATE) ? (
    <form ref={form} onSubmit={(evt) => evt.preventDefault()} onKeyDown={preventSubmitOnEnter}>
      <TopBar
        onBackClick={() => navigate(-1)}
        title={
          location.state?.reason
            ? t('pages.absenceReasons.create.editHeadline')
            : t('pages.absenceReasons.create.createHeadline')
        }
      >
        <SaveButtons buttonRef={button} handleSubmit={handleSubmit} />
      </TopBar>
      <Box>
        <>
          <Input
            type="text"
            value={absenceReason.name}
            label={t('pages.absenceReasons.create.name')}
            onChange={(name) => setAbsenceReason({ ...absenceReason, name })}
          />
          <div className="three-columns">
            <Input
              label={t('pages.absenceReasons.create.color')}
              type="color"
              onChange={(color) => setAbsenceReason({ ...absenceReason, color })}
              value={absenceReason.color}
            />
            <Dropdown
              multi
              selectedMultiOptions={absenceReason.validCompanies}
              options={generateDropdownOptions(loadedCompanies, 'name', 'id')}
              onChangeMultiple={(validCompanies) =>
                setAbsenceReason({ ...absenceReason, validCompanies })
              }
              label={t('pages.absenceReasons.create.validCompanies')}
            />
            <div className="absence-reason-create__settings">
              <p className="absence-reason-create__settings__headline">
                {t('pages.absenceReasons.create.settings')}
              </p>
              <div>
                <Checkbox
                  onCheck={(creatingTimeTracking) =>
                    setAbsenceReason({ ...absenceReason, creatingTimeTracking })
                  }
                  isChecked={absenceReason.creatingTimeTracking}
                  label={t('pages.absenceReasons.create.createTracking')}
                />
                <Checkbox
                  onCheck={(holidayReason) => setAbsenceReason({ ...absenceReason, holidayReason })}
                  isChecked={absenceReason.holidayReason}
                  label={t('pages.absenceReasons.create.holidayReason')}
                />
                <Checkbox
                  label={t('pages.absenceReasons.create.active')}
                  onCheck={(disabled) => {
                    setAbsenceReason({ ...absenceReason, disabled: !disabled });
                  }}
                  isChecked={!absenceReason.disabled}
                />
                <Checkbox
                  label={t('pages.absenceReasons.create.azkReduction')}
                  onCheck={(azkReduction) => setAbsenceReason({ ...absenceReason, azkReduction })}
                  isChecked={absenceReason.azkReduction}
                />
              </div>
            </div>
          </div>
        </>
      </Box>
      <Box title={t('pages.absenceReasons.create.translations')}>
        <Table
          rows={translationRows}
          header={
            t('pages.absenceReasons.create.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </form>
  ) : (
    <NotAllowed neccessaryRight={Right.ABSENCE_REASON_CREATE} />
  );
};

export default AbsenceReasonCreate;
